import * as React from 'react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { NativeDataKey } from 'bundles/promo-template/components/NativeDataContext';
import { DOMAINS } from 'bundles/promo-template/constants/navigation';
import DataSourceLoader from 'bundles/promo-template/contentful/core/DataSourceLoader';
import ModuleRenderer from 'bundles/promo-template/contentful/modules/ModuleRenderer';
import type { PromoTemplatePageData } from 'bundles/promo-template/queries/hooks/usePageContentQuery';
import { CourseraMetatags } from 'bundles/seo';
import PromoTemplateWebPageSchemaMarkup from 'bundles/seo/components/PromoTemplateWebPageSchemaMarkup';

type Props = {
  slug: string;
  domain: string;
  pageData?: PromoTemplatePageData;
};

// WARN: DO NOT call `React.useContext(NativeDataContext)` in `PageContent`!!.
// This page renders the data sources.
// If you call `React.useContext(NativeDataContext)` here,
// it will cause an infinite re-render loop.
export default function PageContent({ slug, domain, pageData }: Props) {
  if (!pageData) {
    return null;
  }

  const { title, blocks, seoDescription, seoImageUrl, seoCanonical, preventSearchEngine, seoTitle, collectionsData } =
    pageData;

  // TODO:
  // 'program' data source is loaded by default, since it's naturally provided by the EnterpriseExploreDomain.
  // If we have other data requirements that are NOT common across all pages, follow these steps:
  //   1. Add a field `dataSources` to the **PromoTemplate - Page Content** content type (and other page-level content types).
  //   2. Add the field to the GraphQL query in `PageContent.ts`.
  //   3. Uncomment the following line to replace the initial `dataSources` array.
  // const dataSources = page.dataSources.map((ds) => ds?.type).filter(Boolean) as NativeDataKey[];
  const dataSources: NativeDataKey[] = [];

  // set program as the default data source for enterprise explore domain
  if (domain === DOMAINS.enterpriseExplore) {
    dataSources.push('program');
  }

  const dynamicPageTitle = `${seoTitle || title}`;

  return (
    <TrackedDiv
      withVisibilityTracking
      trackClicks={false}
      trackingName="promo_template_page"
      requireFullyVisible={false}
      className="rc-PromoTemplatePage"
    >
      <PromoTemplateWebPageSchemaMarkup data={pageData} />
      <CourseraMetatags
        metaNameAndProperties={{
          title: dynamicPageTitle,
          ogAndTwitterTitle: dynamicPageTitle,
          description: seoDescription,
          disableCourseraDescriptionSuffix: !!seoDescription,
          absoluteImageHref: seoImageUrl,
          ...(preventSearchEngine ? { disableCrawlerIndexing: true } : { enableCrawlerIndexing: true }),
        }}
        paginationProperties={{ canonicalLinkHrefOverride: seoCanonical ?? undefined }}
      />

      <DataSourceLoader dataSources={dataSources} />

      <ModuleRenderer
        modules={blocks}
        slug={slug}
        collectionsData={collectionsData}
        disableCrawling={preventSearchEngine}
      />
    </TrackedDiv>
  );
}
